import type {
  EventAppModules,
  EventModuleKey,
  EventModuleOverride,
  ResolvedEventModule,
} from '../types/eventModules.js';
import { RELEASE_CAPABILITIES } from '../constants/releasePolicy.js';

export const EVENT_MODULE_CATALOG: readonly ResolvedEventModule[] = [
  { key: 'agenda', enabled: true, label: 'Agenda', order: 10 },
  { key: 'papers', enabled: true, label: 'Papers', order: 20 },
  { key: 'speakers', enabled: true, label: 'Speakers', order: 30 },
  { key: 'exhibitors', enabled: true, label: 'Exhibitors', order: 40 },
  { key: 'sponsors', enabled: false, label: 'Sponsors', order: 41 },
  { key: 'industry_partners', enabled: false, label: 'Industry Partners', order: 42 },
  { key: 'committee', enabled: false, label: 'Organizing Committee', order: 43 },
  { key: 'attendees', enabled: true, label: 'Attendees', order: 50 },
  { key: 'venue_map', enabled: true, label: 'Venue Map', order: 60 },
  { key: 'messaging', enabled: false, label: 'Messages', order: 70 },
  { key: 'ai_copilot', enabled: true, label: 'AI Copilot', order: 80 },
  { key: 'notes', enabled: true, label: 'Notes', order: 90 },
  { key: 'recap', enabled: true, label: 'Recap', order: 100 },
];

export function resolveEventModules(
  overrides?: EventAppModules,
): ResolvedEventModule[] {
  return EVENT_MODULE_CATALOG
    .map((module) => {
      const override = overrides?.[module.key];
      return {
        ...module,
        enabled: module.key === 'messaging' && !RELEASE_CAPABILITIES.peerMessagingAvailable
          ? false
          : typeof override?.enabled === 'boolean' ? override.enabled : module.enabled,
        label: typeof override?.label === 'string' ? override.label : module.label,
        order: typeof override?.order === 'number' ? override.order : module.order,
      };
    })
    .sort((left, right) => left.order - right.order);
}

export function isEventModuleEnabled(
  overrides: EventAppModules | undefined,
  key: EventModuleKey,
): boolean {
  if (key === 'messaging' && !RELEASE_CAPABILITIES.peerMessagingAvailable) return false;
  const enabled = overrides?.[key]?.enabled;
  if (typeof enabled === 'boolean') return enabled;
  return EVENT_MODULE_CATALOG.find((module) => module.key === key)?.enabled ?? false;
}

export function getEventModuleLabel(
  overrides: EventAppModules | undefined,
  key: EventModuleKey,
): string {
  const defaultModule = EVENT_MODULE_CATALOG.find((module) => module.key === key);
  const label = overrides?.[key]?.label;
  return typeof label === 'string' ? label : defaultModule?.label ?? key;
}

export function isPaperAIEnabled(overrides: EventAppModules | undefined): boolean {
  return overrides?.ai_copilot?.paper_ai !== false;
}

export function isSessionPrepEnabled(
  overrides: EventAppModules | undefined,
  sessionType: string | null | undefined,
): boolean {
  const allowedTypes = overrides?.ai_copilot?.session_prep_types;
  return !Array.isArray(allowedTypes) || allowedTypes.includes(sessionType ?? '');
}

export function mergeEventModuleOverrides(
  current: EventAppModules | undefined,
  edits: Record<string, EventModuleOverride>,
): EventAppModules {
  const merged: EventAppModules = Object.fromEntries(
    Object.entries(current ?? {}).map(([key, value]) => [key, { ...value }]),
  );

  for (const [key, edit] of Object.entries(edits)) {
    merged[key] = { ...merged[key], ...edit };
  }

  return merged;
}
